<template>
  <BaseModal
    useForm
    :heading="$t('register.gifty.heading')"
    small
    @submit="handleSubmit"
    @close="emit('close')"
  >
    <Flex direction="column">
      <BaseAlert
        v-if="lookupError"
        :color="LOOKUP_ERRORS[lookupError.type].color"
        :text="$t(LOOKUP_ERRORS[lookupError.type].text, { date: lookupError.date })"
        icon="alert"
        v-test="`register-gifty-error-${lookupError.type}`"
      />
      <Flex
        v-if="!card"
        align="end"
      >
        <Flex
          direction="column"
          grow
          @keydown.enter.capture.stop.prevent="lookup"
        >
          <BaseInput
            v-model="code"
            :label="$t('register.gifty.card_code')"
            :placeholder="$t('register.gifty.code_placeholder')"
            :disabled="looking"
            focus
            v-test="'register-gifty-code'"
          />
        </Flex>
        <BaseButton
          :loading="looking"
          :disabled="!normalizedCode"
          @click="lookup"
          v-test="'register-gifty-lookup'"
        >
          {{ lookupError?.type === 'unavailable'
              ? $t('register.gifty.try_again')
              : $t('register.gifty.look_up') }}
        </BaseButton>
      </Flex>
      <template v-else>
        <BaseCard>
          <Flex
            align="center"
            :gap="2"
          >
            <Flex
              :gap="2"
              justify="between"
              wrap
              grow
            >
              <Flex
                direction="column"
                :gap="0.25"
              >
                <BaseHeading size="xs">
                  {{ $t('register.gifty.card') }}
                </BaseHeading>
                <BaseText v-test="'register-gifty-card-code'">
                  {{ formattedCode }}
                </BaseText>
              </Flex>
              <Flex
                direction="column"
                :gap="0.25"
              >
                <BaseHeading size="xs">
                  {{ $t('global.balance') }}
                </BaseHeading>
                <BaseText v-test="'register-gifty-balance'">
                  {{ filters.currency(card.balance) }}
                </BaseText>
              </Flex>
              <Flex
                direction="column"
                :gap="0.25"
              >
                <BaseHeading size="xs">
                  {{ $t('global.expires') }}
                </BaseHeading>
                <BaseText v-test="'register-gifty-expires'">
                  {{ card.expiresAt
                      ? filters.date(card.expiresAt)
                      : $t('register.gifty.no_expiry') }}
                </BaseText>
              </Flex>
            </Flex>
            <BaseButton
              color="inverted"
              @click="reset"
              v-test="'register-gifty-change'"
            >
              {{ $t('global.actions.change') }}
            </BaseButton>
          </Flex>
        </BaseCard>
        <div ref="amountField">
          <BaseInput
            v-model="amount"
            type="currency"
            :label="$t('register.gifty.amount_to_use')"
            required
            :error="amountError"
            v-test="'register-gifty-amount'"
          />
        </div>
      </template>
    </Flex>

    <template #footer>
      <BaseButton
        color="inverted"
        @click="emit('close')"
        v-test="'register-gifty-close'"
      >
        {{ $t('global.actions.cancel') }}
      </BaseButton>
      <BaseButton
        submitForm
        :disabled="!card || !!amountError"
        v-test="'register-gifty-save'"
      >
        {{ submitLabel }}
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script
  setup
  lang="ts"
>
import { storeToRefs } from 'pinia';
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import apolloClient from '@/apollo/client';
import Flex from '@/components/layout/Flex.vue';
import dayjs from '@/dayjs';
import filters from '@/filters';
import { useRegisterOrderStore } from '@/modules/register/stores/order';
import { type GiftyGiftcard, GiftyGiftcardLookupStatus } from '@/types';
import { GET_GIFTY_GIFTCARD } from './graphql';

const props = defineProps<{
  paymentAmount: number;
}>();

const emit = defineEmits<{
  (
    e: 'submit',
    payment: { type: 'gifty'; amount: number; giftyCode: string; name: string },
  ): void;
  (e: 'close'): void;
}>();

type GiftyCard = Pick<GiftyGiftcard, 'balance' | 'expiresAt' | 'isRedeemable'>;

const LOOKUP_ERRORS = {
  'already-used': {
    color: 'warning',
    text: 'register.gifty.errors.already_used',
  },
  'not-found': { color: 'error', text: 'register.gifty.errors.not_found' },
  unavailable: { color: 'error', text: 'register.gifty.errors.unavailable' },
  expired: { color: 'error', text: 'register.gifty.errors.expired' },
  'no-balance': { color: 'warning', text: 'register.gifty.errors.no_balance' },
  'not-redeemable': {
    color: 'error',
    text: 'register.gifty.errors.not_redeemable',
  },
} as const;

type LookupError = { type: keyof typeof LOOKUP_ERRORS; date?: string };

const { t } = useI18n();
const { order } = useRegisterOrderStore();
const { outstandingAmount } = storeToRefs(useRegisterOrderStore());

const code = ref('');
const card = ref<GiftyCard | null>(null);
const amount = ref(0);
const looking = ref(false);
const lookupError = ref<LookupError | null>(null);
const amountField = useTemplateRef<HTMLDivElement>('amountField');

const normalizedCode = computed(() =>
  code.value.replace(/[^a-z0-9]/gi, '').toUpperCase(),
);
const formattedCode = computed(() =>
  normalizedCode.value.replace(/(.{4})(?=.)/g, '$1 '),
);
const maxAmount = computed(() =>
  Math.min(outstandingAmount.value, card.value?.balance ?? 0),
);
const amountError = computed(() => {
  if (!card.value) {
    return undefined;
  }

  if (amount.value > maxAmount.value) {
    return t(
      outstandingAmount.value < card.value.balance
        ? 'register.gifty.errors.max_amount_sale'
        : 'register.gifty.errors.max_amount',
      { amount: filters.currency(maxAmount.value) },
    );
  }

  return amount.value > 0 ? undefined : t('register.gifty.errors.no_amount');
});
const submitLabel = computed(() =>
  card.value && !amountError.value
    ? t('register.gifty.use_amount', { amount: filters.currency(amount.value) })
    : t('register.gifty.use_card'),
);

watch(code, () => {
  lookupError.value = null;
});

const isAlreadyUsed = () =>
  order.transactions.some(
    (transaction) =>
      !transaction.id &&
      transaction.type === 'GIFTY' &&
      transaction.giftyCode === normalizedCode.value,
  );

const cardError = (giftcard: GiftyCard): LookupError | null => {
  if (
    !giftcard.isRedeemable &&
    giftcard.expiresAt &&
    dayjs(giftcard.expiresAt).isBefore(dayjs())
  ) {
    return { type: 'expired', date: filters.date(giftcard.expiresAt) };
  }

  if (giftcard.balance <= 0) {
    return { type: 'no-balance' };
  }

  if (!giftcard.isRedeemable) {
    return { type: 'not-redeemable' };
  }

  return null;
};

const lookup = async () => {
  if (!normalizedCode.value || looking.value) {
    return;
  }

  if (isAlreadyUsed()) {
    lookupError.value = { type: 'already-used' };
    return;
  }

  lookupError.value = null;
  looking.value = true;

  try {
    const { data } = await apolloClient.query<{
      giftyGiftcard: {
        status: GiftyGiftcardLookupStatus;
        giftcard?: GiftyCard | null;
      };
    }>({
      query: GET_GIFTY_GIFTCARD,
      variables: { giftyCode: normalizedCode.value },
      fetchPolicy: 'network-only',
    });
    const { status, giftcard } = data.giftyGiftcard;

    if (status === GiftyGiftcardLookupStatus.Found && giftcard) {
      lookupError.value = cardError(giftcard);

      if (!lookupError.value) {
        card.value = giftcard;
        amount.value = Math.min(props.paymentAmount, maxAmount.value);
        await nextTick();
        amountField.value?.querySelector('input')?.focus();
      }
    } else if (status === GiftyGiftcardLookupStatus.NotFound) {
      lookupError.value = { type: 'not-found' };
    } else {
      lookupError.value = { type: 'unavailable' };
    }
  } catch {
    lookupError.value = { type: 'unavailable' };
  } finally {
    looking.value = false;
  }
};

const reset = () => {
  card.value = null;
  code.value = '';
};

const handleSubmit = () => {
  if (!card.value || amountError.value) {
    return;
  }

  emit('submit', {
    type: 'gifty',
    amount: amount.value,
    giftyCode: normalizedCode.value,
    name: normalizedCode.value,
  });
};
</script>
